import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "~/components/ui/toaster";
import { useAuth } from "../hooks/useAuth";
import { useConsent } from "./consent";
import { routes } from "../lib/routes";
import { cn } from "../lib/utils";
import { Avatar } from "./ui/avatar";
import { Button } from "./ui/button";
import { Icon } from "./ui/icons/icon";
import { Separator } from "./ui/separator";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "./ui/sheet";
import { Switch } from "./ui/switch";

export function UserMenu() {
  const { user, signOut } = useAuth();
  const { consent, accept, reject } = useConsent();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const analyticsEnabled = consent === "accepted";
  const toggleAnalytics = (enabled: boolean) => {
    if (enabled) {
      accept();
      toast.success("Analytics enabled");
    } else {
      reject();
      toast.success("Analytics disabled");
    }
  };

  const displayName = user.profile?.displayName || user.email;

  const handleSignOut = async () => {
    setOpen(false);
    try {
      await signOut();
    } catch {
      toast.error("Couldn't sign out. Please try again.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="gap-2 rounded-full size-fit p-1 has-[>svg]:px-1 sm:has-[>svg]:pr-2.5"
      >
        <Avatar src={null} alt={displayName} size="sm" className="size-8" />
        <Icon i="menu" className="size-6 hidden sm:block" />
      </Button>

      <SheetContent className="p-8">
        <SheetTitle className="sr-only">User menu</SheetTitle>

        <SheetClose asChild>
          <Button variant="outline" size="icon" className="rounded-full shadow-none">
            <Icon i="back" className="size-5" />
            <span className="sr-only">Close menu</span>
          </Button>
        </SheetClose>

        <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-border py-5">
          <Avatar src={null} alt={displayName} size="lg" className="border-2 border-primary" />
          {displayName}
        </div>

        <nav className="mt-8 flex flex-col">
          <NavItem to={routes.to.home()} active={location.pathname === "/"} onClick={() => setOpen(false)}>
            Home
          </NavItem>
          <NavItem
            to={routes.to.maps.root()}
            active={location.pathname.startsWith("/maps")}
            onClick={() => setOpen(false)}
          >
            My Adventure Maps
          </NavItem>
          <Separator className="my-4" />
          <div className="flex items-center justify-between py-1">
            <label htmlFor="analytics-toggle" className="font-medium cursor-pointer">
              Analytics
            </label>
            <Switch id="analytics-toggle" checked={analyticsEnabled} onCheckedChange={toggleAnalytics} />
          </div>
          <NavItem onClick={handleSignOut}>
            <Icon i="log-out" className="size-5" />
            Sign out
          </NavItem>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

const navItemStyles = "flex items-center gap-3 py-1 font-medium transition-colors hover:text-primary";

type NavItemProps = {
  onClick: () => void;
  children: React.ReactNode;
} & ({ to: string; active: boolean } | { to?: never; active?: never });

function NavItem({ to, active, onClick, children }: NavItemProps) {
  const className = cn(navItemStyles, { "text-primary": active });

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={className}>
      {children}
    </button>
  );
}
